import { useCallback } from "react";
import { ErrorCode, FileRejection, useDropzone } from "react-dropzone";

import { toastActions } from "src/design-system/Toast/utils";
import {
  FILE_EXTENSIONS,
  FILE_TYPES,
  humanReadableSize,
  MAX_FILE_SIZE_BYTES,
} from "src/utils/files";

const getErrorMessage = (errorCode: ErrorCode | string) => {
  switch (errorCode) {
    case ErrorCode.FileInvalidType:
      return `File is not a valid file type. Supported file types: ${Object.values(FILE_EXTENSIONS).join(", ")}`;
    case ErrorCode.FileTooLarge:
      return `File is larger than ${humanReadableSize(MAX_FILE_SIZE_BYTES)}`;
    default:
      return "Error uploading File";
  }
};

const getAcceptedFileTypes = () => {
  const mimeTypes = Object.keys(FILE_TYPES);
  const extensions = Object.values(FILE_EXTENSIONS).map((ext) => `.${ext}`);

  const accept: Record<string, string[]> = {};

  mimeTypes.forEach((mimeType) => {
    accept[mimeType] = extensions;
  });

  return accept;
};

const fileValidator = (file: File) => {
  if ("name" in file) {
    const extension = file.name.split(".").pop();
    if (!extension) {
      return {
        code: "FileExtensionMissing",
        message: "File must have an extension. E.g. .pdf, .jpg, or .png",
      };
    }
    if (!Object.values(FILE_EXTENSIONS).includes(extension)) {
      return {
        code: ErrorCode.FileInvalidType,
        message: `Invalid file type. Supported types: ${Object.values(FILE_EXTENSIONS).join(", ")}`,
      };
    }
  }
  return null;
};

type Props = {
  onFileSelect: (file: File, key: string) => void;
  children: React.ReactNode;
  disabled?: boolean;
  className?: string;
  uploadKey: string;
};

export const FileUploadController: React.FC<Props> = ({
  onFileSelect,
  disabled,
  className,
  children,
  uploadKey,
}) => {
  const handleOnDrop = useCallback(
    (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
      if (acceptedFiles.length) {
        onFileSelect(acceptedFiles[0], uploadKey);
      }

      if (rejectedFiles.length > 1) {
        toastActions.failure({ title: "Only 1 file allowed" });
        return;
      }

      rejectedFiles.forEach((rejection) => {
        const errorMessage = getErrorMessage(rejection.errors[0].code);
        toastActions.failure({ title: errorMessage });
      });
    },
    [onFileSelect, uploadKey],
  );

  const { getRootProps, getInputProps } = useDropzone({
    onDrop: handleOnDrop,
    useFsAccessApi: false,
    maxFiles: 1,
    maxSize: MAX_FILE_SIZE_BYTES,
    accept: getAcceptedFileTypes(),
    validator: fileValidator,
    disabled,
  });
  return (
    <div {...getRootProps({ className, "data-loc": "file-upload-dropzone" })}>
      {children}
      <input {...getInputProps()} data-loc="file-upload" />
    </div>
  );
};
